/**
 * Swamp hut (vanilla SwampHutPiece): a spruce-plank hut on oak-log stilts
 * above the water, with a porch on the front, a crafting table and the witch's
 * cauldron (a cobblestone block here — no cauldrons, and loose water would flow) inside.
 */

import { SEA_LEVEL } from '../../constants';
import { AIR } from '../../blocks';
import { Biome } from '../biomes';
import type { TerrainGenerator } from '../terrain';
import { block, blueprint, cell, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType } from './kit';

const HUT = blueprint({
  P: block('Spruce Planks'), L: block('Oak Log'), T: block('Crafting Table'),
  C: cell.chest(F.NV), K: block('Cobblestone'), D: cell.door('Spruce Door', F.PV),
}, [
  // floor deck with the porch (rows 0..1) in front
  ['PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP'],
  ['L.....L', '.......', 'LPPDPPL', 'P.....P', 'P.....P', 'P.K..TP', 'P.....P', 'PC....P', 'LPPPPPL'],
  ['L.....L', '.......', 'LPP PPL', 'P.....P', '.......', 'P.....P', 'P.....P', 'P.....P', 'LPPPPPL'],
  ['L.....L', '.......', 'LPPPPPL', 'P.....P', 'P.....P', 'P.....P', 'P.....P', 'P.....P', 'LPPPPPL'],
  ['PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP', 'PPPPPPP'],
]);

function swampHut(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (c.biome !== Biome.Swamp && c.biome !== Biome.MangroveSwamp) return null;
  const y = Math.max(c.height, SEA_LEVEL) + 2;
  return [centredPiece(x, z, int(rnd, 4), 7, 9, y, false, seedOf(rnd), (b) => {
    b.fill(-1, y + 1, -1, 7, y + 6, 9, AIR);
    // stilts down into the swamp at the four corners of the cabin
    for (const [u, v] of [[0, 2], [6, 2], [0, 8], [6, 8], [0, 0], [6, 0]]) b.foundation(u, y - 1, v, block('Oak Log'), 12);
    b.stamp(HUT, 0, y, 0);
  })];
}

export const SWAMP_HUT: StructureType = {
  name: 'Swamp Hut', spacing: 32, separation: 8, salt: 14357620, reach: 1, generate: swampHut,
};
